<template>
  <div
    class="relative flex flex-col min-w-0 break-words w-full mb-6 shadow-lg rounded-lg overflow-hidden bg-blueGray-700"
  >
    <div class="rounded-t mb-0 px-4 py-3 bg-transparent">
      <div class="flex flex-wrap items-center">
        <div class="relative w-full max-w-full flex-grow flex-1">
          <h6 class="uppercase text-blueGray-100 mb-1 text-xs font-semibold">
            Overview
          </h6>
          <h2 class="text-white text-xl font-semibold">
            活动创建数量
          </h2>
        </div>
      </div>
    </div>
    <div class="p-4 flex-auto">
      <!-- Chart -->
      <div class="relative h-350-px">
        <canvas id="line-chart"></canvas>
      </div>
    </div>
  </div>
</template>
<script>
import { Chart, registerables } from "chart.js";
import { getAdminApiUrl } from "@/api/luckdraw.js";
import { getUserToken } from "@/utils/auth.js";
// 注册所有Chart.js组件
Chart.register(...registerables);

// 生成模拟数据 - 每天每小时的活动创建数量
function generateHourlyData(baseValue, variance) {
  const data = [];
  for (let i = 0; i < 24; i++) {
    // 模拟每小时的活动创建趋势：工作时间(9-18点)较高，夜间较低
    let hourlyFactor = 1;
    if (i >= 9 && i <= 18) {
      hourlyFactor = 1.5 + Math.random() * 0.5; // 工作时间较高
    } else if (i >= 6 && i < 9) {
      hourlyFactor = 1.2 + Math.random() * 0.3; // 早晨逐渐增加
    } else if (i > 18 && i <= 22) {
      hourlyFactor = 1.1 + Math.random() * 0.4; // 傍晚逐渐下降
    } else {
      hourlyFactor = 0.3 + Math.random() * 0.4; // 夜间较低
    }
    const value = Math.floor(baseValue * hourlyFactor + (Math.random() - 0.5) * variance);
    data.push(Math.max(0, value));
  }
  return data;
}

// 获取今天日期字符串
function getTodayString() {
  const today = new Date();
  return `${today.getMonth() + 1}月${today.getDate()}日`;
}

// 获取昨天日期字符串
function getYesterdayString() {
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  return `${yesterday.getMonth() + 1}月${yesterday.getDate()}日`;
}

// 获取7天前日期字符串
function getSevenDaysAgoString() {
  const date = new Date();
  date.setDate(date.getDate() - 7);
  return `${date.getMonth() + 1}月${date.getDate()}日`;
}

// 从API获取数据
async function fetchHourlyStats() {
  const token = getUserToken();
  const response = await fetch(getAdminApiUrl('/stat/task/hourly'), {
    method: 'POST',
    headers: {
            'Token': `${token}`,
            'Accept': 'application/json',
            'Content-Type': 'application/json'
    },
    credentials: 'include',
    body: JSON.stringify({ days: 3 })
  });
  const result = await response.json();
  if (result.sta === 0 && result.data) {
    return result.data;
  }
  // 接口异常时返回null，由下方使用模拟数据
  return null;
}

export default {
  mounted: function () {
    this.$nextTick(async function () {
      // 从API获取数据
      const apiData = await fetchHourlyStats();

      let config;

      if (apiData && apiData.datasets && apiData.datasets.length > 0) {
        // 使用API返回的数据
        config = {
          type: "line",
          data: {
            labels: apiData.hours,
            datasets: apiData.datasets.map(ds => ({
              label: ds.label,
              backgroundColor: ds.color,
              borderColor: ds.color,
              data: ds.data,
              fill: false,
              tension: 0.3,
            }))
          },
          options: {
            maintainAspectRatio: false,
            responsive: true,
            plugins: {
              legend: {
                labels: { color: "white" },
                align: "end",
                position: "bottom",
              },
              tooltip: {
                mode: "index",
                intersect: false,
                callbacks: {
                  label: function(context) {
                    return context.dataset.label + ': ' + context.parsed.y + ' 个活动';
                  }
                }
              },
            },
            interaction: {
              mode: "nearest",
              intersect: true,
            },
            scales: {
              x: {
                ticks: {
                  color: "rgba(255,255,255,.7)",
                  maxRotation: 0,
                  autoSkip: true,
                  maxTicksLimit: 12,
                },
                display: true,
                title: { display: true, text: "时间", color: "white" },
                grid: { display: false, borderDash: [2], borderDashOffset: 2, color: "rgba(33, 37, 41, 0.3)", zeroLineColor: "rgba(0, 0, 0, 0)", zeroLineBorderDash: [2], zeroLineBorderDashOffset: 2 },
              },
              y: {
                ticks: {
                  color: "rgba(255,255,255,.7)",
                  callback: function(value) { return value + ' 个'; }
                },
                display: true,
                title: { display: true, text: "活动创建数量", color: "white" },
                grid: { borderDash: [3], borderDashOffset: 3, drawBorder: false, color: "rgba(255, 255, 255, 0.15)", zeroLineColor: "rgba(33, 37, 41, 0)", zeroLineBorderDash: [2], zeroLineBorderDashOffset: 2 },
              },
            },
          },
        };
      } else {
        // 使用模拟数据（接口异常时降级）
        const todayData = generateHourlyData(50, 30);
        const yesterdayData = generateHourlyData(45, 25);
        const sevenDaysAgoData = generateHourlyData(35, 20);
        const hours = Array.from({ length: 24 }, (_, i) => `${i.toString().padStart(2, '0')}:00`);

        config = {
          type: "line",
          data: {
            labels: hours,
            datasets: [
              {
                label: getTodayString(),
                backgroundColor: "#10b981",
                borderColor: "#10b981",
                data: todayData,
                fill: false,
                tension: 0.3,
              },
              {
                label: getYesterdayString(),
                fill: false,
                backgroundColor: "#3b82f6",
                borderColor: "#3b82f6",
                data: yesterdayData,
                tension: 0.3,
              },
              {
                label: getSevenDaysAgoString(),
                fill: false,
                backgroundColor: "#f59e0b",
                borderColor: "#f59e0b",
                data: sevenDaysAgoData,
                tension: 0.3,
              },
            ],
          },
          options: {
            maintainAspectRatio: false,
            responsive: true,
            plugins: {
              legend: {
                labels: { color: "white" },
                align: "end",
                position: "bottom",
              },
              tooltip: {
                mode: "index",
                intersect: false,
                callbacks: {
                  label: function(context) {
                    return context.dataset.label + ': ' + context.parsed.y + ' 个活动';
                  }
                }
              },
            },
            interaction: {
              mode: "nearest",
              intersect: true,
            },
            scales: {
              x: {
                ticks: {
                  color: "rgba(255,255,255,.7)",
                  maxRotation: 0,
                  autoSkip: true,
                  maxTicksLimit: 12,
                },
                display: true,
                title: { display: true, text: "时间", color: "white" },
                grid: { display: false, borderDash: [2], borderDashOffset: 2, color: "rgba(33, 37, 41, 0.3)", zeroLineColor: "rgba(0, 0, 0, 0)", zeroLineBorderDash: [2], zeroLineBorderDashOffset: 2 },
              },
              y: {
                ticks: {
                  color: "rgba(255,255,255,.7)",
                  callback: function(value) { return value + ' 个'; }
                },
                display: true,
                title: { display: true, text: "活动创建数量", color: "white" },
                grid: { borderDash: [3], borderDashOffset: 3, drawBorder: false, color: "rgba(255, 255, 255, 0.15)", zeroLineColor: "rgba(33, 37, 41, 0)", zeroLineBorderDash: [2], zeroLineBorderDashOffset: 2 },
              },
            },
          },
        };
      }

      var ctx = document.getElementById("line-chart").getContext("2d");
      window.myLine = new Chart(ctx, config);
    });
  },
};
</script>
